import { useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { ArrowLeft } from "lucide-react";
import { useT } from "@/i18n/LanguageProvider";
import { PRICING_NOTICE_FR } from "@/config/pricing";

const SECTIONS = [
  {
    h: "Objet et champ d'application",
    body: (
      <>
        <p>
          Les présentes conditions générales encadrent les prestations
          proposées sous la marque HorsIndex : cartographie de l'exposition
          publique, accompagnement au retrait ou au masquage d'informations,
          demandes de déréférencement et prestations associées.
        </p>
        <p className="mt-4">
          L'activité est développée par Simon Marie dans le cadre d'un Contrat
          d'appui au projet d'entreprise (CAPE) conclu avec Optéos, dont les
          coordonnées figurent dans les{" "}
          <Link to="/mentions-legales" className="text-[#181A1D] underline-link">
            mentions légales
          </Link>. Les prestations sont contractualisées et facturées par
          l'intermédiaire d'Optéos dans le cadre de ce contrat.
        </p>
      </>
    ),
  },
  {
    h: "Devis et formation du contrat",
    body: (
      <>
        <p>
          Les demandes effectuées depuis le site, quel que soit le formulaire
          utilisé, ne constituent pas une commande ferme. Elles ouvrent un
          échange préalable destiné à examiner la situation présentée et à
          vérifier que la demande relève du périmètre d'intervention.
        </p>
        <p className="mt-4">
          Toute prestation donne lieu à un devis ou à un document contractuel
          écrit, précisant le contenu de la mission, le prix, les délais
          indicatifs et les conditions applicables. Le contrat est formé par
          l'acceptation écrite de ce document par le client.
        </p>
      </>
    ),
  },
  {
    h: "Prix et facturation",
    body: (
      <>
        <p>{PRICING_NOTICE_FR}</p>
        <p className="mt-4">
          La facturation est établie par Optéos dans le cadre du CAPE. Les
          modalités de règlement (échéance, moyens de paiement) sont précisées
          sur le devis et la facture. Aucun paiement n'est demandé ni collecté
          directement sur le site.
        </p>
      </>
    ),
  },
  {
    h: "Délais et exécution",
    body: (
      <>
        <p>
          Les délais mentionnés dans le devis sont indicatifs, sauf engagement
          exprès contraire. Certaines démarches dépendent de tiers (moteurs de
          recherche, éditeurs de sites, plateformes, autorités) dont les délais
          de traitement ne peuvent être garantis.
        </p>
        <p className="mt-4">
          Le client est tenu informé de l'avancement des démarches engagées.
          Il s'engage à fournir les informations et documents nécessaires à
          l'exécution de la prestation, et à signaler tout élément susceptible
          d'en modifier le périmètre.
        </p>
      </>
    ),
  },
  {
    h: "Nature des obligations",
    body: (
      <p>
        Les prestations relèvent d'une obligation de moyens. Les décisions de
        retrait, de masquage ou de déréférencement appartiennent aux tiers
        concernés (moteurs de recherche, éditeurs, hébergeurs) ou, le cas
        échéant, aux autorités compétentes. Aucun résultat déterminé ne peut
        être garanti. L'engagement porte sur la qualité de l'analyse, la
        rigueur des démarches et leur documentation.
      </p>
    ),
  },
  {
    h: "Droit de rétractation",
    body: (
      <>
        <p>
          Lorsque le client est un consommateur et que le contrat est conclu à
          distance, il dispose d'un délai de rétractation de quatorze jours à
          compter de la conclusion du contrat, conformément aux articles
          L. 221-18 et suivants du Code de la consommation.
        </p>
        <p className="mt-4">
          Si le client demande expressément que l'exécution de la prestation
          commence avant l'expiration de ce délai, il peut être redevable du
          prix correspondant au service déjà fourni au moment de sa
          rétractation. Le droit de rétractation ne peut plus être exercé pour
          une prestation pleinement exécutée avec son accord exprès préalable.
        </p>
      </>
    ),
  },
  {
    h: "Données personnelles",
    body: (
      <p>
        Les données transmises dans le cadre des échanges et des prestations
        sont traitées conformément à la{" "}
        <Link to="/politique-confidentialite" className="text-[#181A1D] underline-link">
          politique de confidentialité
        </Link>. Elles sont utilisées uniquement pour les besoins de la
        relation contractuelle et des démarches confiées.
      </p>
    ),
  },
  {
    h: "Droit applicable et médiation",
    body: (
      <>
        <p>
          Les présentes conditions sont soumises au droit français. En cas de
          difficulté, les parties recherchent en priorité une solution amiable.
        </p>
        <p className="mt-4">
          Conformément aux articles L. 612-1 et suivants du Code de la
          consommation, le client consommateur peut recourir gratuitement à un
          médiateur de la consommation en vue de la résolution amiable d'un
          litige. Les coordonnées du médiateur compétent sont communiquées sur
          demande écrite à{" "}
          <a href="mailto:horsindex@gmail.com" className="text-[#181A1D] underline-link">
            horsindex@gmail.com
          </a>{" "}
          et figurent sur les documents contractuels.
        </p>
      </>
    ),
  },
];

export default function ConditionsGenerales() {
  const { t } = useT();

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, []);

  return (
    <div data-testid="page-conditions-generales" className="bg-[#FAF9F7] text-[#181A1D] min-h-screen">
      <Navbar />
      <main className="max-w-3xl mx-auto px-6 lg:px-10 pt-32 lg:pt-40 pb-24">
        <Link
          to="/"
          data-testid="back-home-link"
          className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-[#666A70] hover:text-[#181A1D] mb-12 transition-colors"
        >
          <ArrowLeft size={14} strokeWidth={1.5} /> {t("common.back")}
        </Link>

        <p className="label-eyebrow mb-6">Informations légales</p>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#181A1D] tracking-tight leading-[1.05] font-normal mb-7 text-balance">
          Conditions générales
        </h1>
        <div className="h1-marker mb-12" />

        <div className="space-y-12 font-normal text-[#52565C] leading-relaxed">
          {SECTIONS.map((s) => (
            <section key={s.h}>
              <h2 className="font-serif text-2xl text-[#181A1D] mb-4">{s.h}</h2>
              {s.body}
            </section>
          ))}
        </div>
      </main>
      <Footer />
    </div>
  );
}
